'use client';

import { init as initApm } from '@elastic/apm-rum';

let apmInstance: ReturnType<typeof initApm> | null = null;
let isInitialized = false;
let hasStartedPageLoad = false;

function parseSampleRate(value: string | undefined): number {
  if (!value) return 1;
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return 1;
  if (parsed < 0) return 0;
  if (parsed > 1) return 1;
  return parsed;
}

function buildDistributedTracingOrigins(): Array<string | RegExp> {
  const origins: Array<string | RegExp> = [];
  const rawOrigins = process.env.NEXT_PUBLIC_ELASTIC_APM_DISTRIBUTED_TRACING_ORIGINS;
  if (rawOrigins) {
    rawOrigins
      .split(',')
      .map((origin) => origin.trim())
      .filter(Boolean)
      .forEach((origin) => origins.push(origin));
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    origins.push(window.location.origin);
  }

  const publicApiUrl = process.env.NEXT_PUBLIC_API_PUBLIC_URL;
  if (publicApiUrl) {
    try {
      const url = new URL(publicApiUrl);
      origins.push(`${url.protocol}//${url.host}`);
    } catch {
      // Ignore invalid URL.
    }
  }

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.API_BASE_URL;
  if (apiBaseUrl) {
    try {
      const url = new URL(apiBaseUrl);
      origins.push(`${url.protocol}//${url.host}`);
    } catch {
      // Ignore invalid URL.
    }
  }

  const uniqueStrings = new Set<string>();
  const uniqueOrigins: Array<string | RegExp> = [];
  origins.forEach((origin) => {
    if (typeof origin === 'string') {
      if (uniqueStrings.has(origin)) return;
      uniqueStrings.add(origin);
      uniqueOrigins.push(origin);
      return;
    }
    uniqueOrigins.push(origin);
  });
  return uniqueOrigins;
}

export function initializeElasticRum(pageLoadName?: string) {
  if (isInitialized || typeof window === 'undefined') {
    return apmInstance;
  }

  const enabled = process.env.NEXT_PUBLIC_ELASTIC_APM_ENABLED === 'true';
  if (!enabled) {
    return null;
  }

  const serverUrl = process.env.NEXT_PUBLIC_ELASTIC_APM_SERVER_URL;
  if (!serverUrl) {
    console.warn('[Elastic RUM] Missing NEXT_PUBLIC_ELASTIC_APM_SERVER_URL; skipping initialization.');
    return null;
  }

  const serviceName = process.env.NEXT_PUBLIC_ELASTIC_APM_SERVICE_NAME || 'ui';
  const serviceVersion =
    process.env.NEXT_PUBLIC_ELASTIC_APM_SERVICE_VERSION ||
    process.env.NEXT_PUBLIC_OTEL_SERVICE_VERSION ||
    '1.0.0';
  const environment =
    process.env.NEXT_PUBLIC_ELASTIC_APM_ENVIRONMENT ||
    process.env.NODE_ENV ||
    'production';

  const transactionSampleRate = parseSampleRate(
    process.env.NEXT_PUBLIC_ELASTIC_APM_SAMPLE_RATE
  );

  apmInstance = initApm({
    serviceName,
    serviceVersion,
    serverUrl,
    environment,
    transactionSampleRate,
    breakdownMetrics: true,
    distributedTracingOrigins: buildDistributedTracingOrigins(),
    propagateTracestate: true,
    centralConfig: true,
  });

  if (pageLoadName) {
    apmInstance?.setInitialPageLoadName?.(pageLoadName);
    startElasticRumPageLoad(pageLoadName);
  }

  isInitialized = true;

  console.log('[Elastic RUM] Initialized', {
    serviceName,
    serviceVersion,
    serverUrl,
    environment,
    transactionSampleRate,
  });

  return apmInstance;
}

export function getElasticApm() {
  return apmInstance;
}

export function startElasticRumPageLoad(route: string) {
  if (!apmInstance || hasStartedPageLoad) return;
  const transaction = apmInstance.startTransaction(route || '/', 'page-load', {
    managed: true,
    canReuse: true,
  });
  if (transaction) {
    const endTransaction = () => {
      try {
        transaction.end();
      } catch {
        // Ignore end errors.
      }
    };

    if (document.readyState === 'complete') {
      endTransaction();
    } else {
      window.addEventListener('load', endTransaction, { once: true });
    }
  }
  hasStartedPageLoad = true;
}

export function trackElasticRumRouteChange(route: string) {
  if (!apmInstance) return;
  const routeChangeAsPageLoad =
    process.env.NEXT_PUBLIC_ELASTIC_APM_ROUTE_CHANGE_AS_PAGE_LOAD === 'true';
  const transactionType = routeChangeAsPageLoad ? 'page-load' : 'route-change';
  const transaction = apmInstance.startTransaction(route || '/', transactionType, {
    managed: true,
    canReuse: true,
  });
  if (routeChangeAsPageLoad) {
    transaction?.addLabels?.({ route_change: true });
  }
}
